import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren
} from "@angular/core";
import {Modal} from "../../../../../services/modal.service";
import {Api} from "../../../../../../common/services/api.service";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {HelpersService} from "../../../../../../common/services/helpers.service";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {AppStateService} from "../../../../../../common/services/app-state.service";
import {FormControl} from "@angular/forms";
import {takeUntil} from "rxjs";


@Component({
    selector: "section-custom-label-modal",
    template: `
        <div class="pull-right">
            <mat-slide-toggle [formControl]="multiple" *ngIf="show_multiple_button">
                Print multiple
            </mat-slide-toggle>

            <button mat-raised-button color="accent" (click)="print()" class="margin-left-20">
                <mat-icon>print</mat-icon>
                Print
            </button>

            <button mat-raised-button color="accent" (click)="edit()" *ngIf="modal.params.show_edit_button"
                    class="margin-left-20">
                <mat-icon>edit</mat-icon>
                Edit
            </button>

        </div>
        <h1 class="container-heading">Print label</h1>
        <mat-card *ngIf="labelSettings && labelsData">
            <mat-card-content>
                <div *ngFor="let data of labelsData" class="label">
                    <div class="margin-bottom-10">
                        <mat-checkbox [formControl]="data.print_me">
                            Print me
                        </mat-checkbox>
                    </div>

                    <section-custom-label-renderer #label
                        [width]="labelSettings.size.width"
                        [height]="labelSettings.size.height"
                        [layout]="labelSettings.layout"
                        [data]="data"
                    ></section-custom-label-renderer>
                </div>

            </mat-card-content>
        </mat-card>
    `,
    styles: [
        `.label {
            margin-bottom: 20px
        }`
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabelModalComponent implements OnInit, OnDestroy {

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public modal: Modal.IModal;

    public labelSettings: any = {};

    public labelsData: any[] = [];

    @ViewChildren("label", {read: ElementRef})
    public labels: QueryList<ElementRef>;

    public multiple: FormControl = new FormControl(false);

    public show_multiple_button: boolean = false;

    private load_generation: number = 0;

    public constructor(
        private api3Service: Api3Service,
        private changeDetectorRef: ChangeDetectorRef,
        private spinnerService: SpinnerService
    ) {
    }

    private async getLabelLayout(): Promise<any> {
        this.spinnerService.show();

        const {data}: Api.IResponse = await this.api3Service.get(
            `${AppStateService.getState().section}/custom-labels/${this.modal.params.label_id}`,
        );

        this.spinnerService.hide();

        if (data) {
            this.labelSettings = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private getEntityUrl(): string {
        switch (this.modal.params.type) {
            case "box":
                return "boxes";
            case "pallet":
                return "pallets";
            case "allocation":
                return "inventories/allocations";
            default:
                return "inventories";
        }
    }

    private async getLabelsData(): Promise<any> {
        const generation: number = ++this.load_generation;

        this.spinnerService.show();

        const labels_data: any[][] = await Promise.all(
            this.modal.params.ids.map((id: any) => this.getLabelData(Number(id)))
        );

        this.spinnerService.hide();

        if (generation !== this.load_generation) {
            return;
        }

        this.labelsData = labels_data.flat();
        this.changeDetectorRef.markForCheck();
    }

    private async getLabelData(id: number): Promise<any[]> {
        const {data}: Api.IResponse = await this.api3Service.get(
            `${AppStateService.getState().section}/${this.getEntityUrl()}/${id}/label-data`,
        );

        if (!data) {
            return [];
        }

        const label_data: any = {
            ...data,
            free_text: this.labelSettings.body,
            ...(this.modal.params.data && this.modal.params.data[id] ? this.modal.params.data[id] : {})
        };

        const copies: number = this.multiple.value && label_data.item_qty > 1 ? label_data.item_qty : 1;

        return Array.from({length: copies}, () => ({
            ...label_data,
            item_qty: copies > 1 ? 1 : label_data.item_qty,
            print_me: new FormControl(true)
        }));
    }

    public edit(): void {
        this.modal.response.emit({
            name: "edit",
            value: true
        });
    }

    public print(): void {
        const printContents: string = this.labels
            .filter((label: ElementRef, index: number): boolean => this.labelsData[index]?.print_me?.value !== false)
            .map((label: ElementRef): string => label.nativeElement.outerHTML)
            .join("");

        if (!printContents) {
            return;
        }

        HelpersService.stickerPopUp(printContents);
    }


    public ngOnInit(): void {
        this.show_multiple_button = !["box", "pallet"].includes(this.modal.params.type);

        this.getLabelLayout().then(() => this.getLabelsData());

        this.multiple.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => this.getLabelsData());
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

}
